import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/components/i18n/TranslationProvider';
import {
  AUTH_TURNSTILE_SCRIPT_ID,
  AUTH_TURNSTILE_SCRIPT_URL,
  getAuthTurnstileConfiguration,
} from '@/lib/authTurnstile.js';

let turnstileScriptPromise = null;

const getLoadedTurnstile = () => {
  if (typeof window === 'undefined') return null;
  return typeof window.turnstile?.render === 'function' ? window.turnstile : null;
};

const loadTurnstileScript = () => {
  const loadedTurnstile = getLoadedTurnstile();
  if (loadedTurnstile) {
    return Promise.resolve(loadedTurnstile);
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('turnstile_browser_unavailable'));
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(AUTH_TURNSTILE_SCRIPT_ID);
    const script = existingScript || document.createElement('script');

    const cleanupListeners = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };

    const handleLoad = () => {
      script.dataset.loaded = 'true';
      cleanupListeners();
      const turnstile = getLoadedTurnstile();
      if (turnstile) {
        resolve(turnstile);
        return;
      }
      turnstileScriptPromise = null;
      reject(new Error('turnstile_api_unavailable'));
    };

    const handleError = () => {
      cleanupListeners();
      turnstileScriptPromise = null;
      reject(new Error('turnstile_script_failed'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (existingScript) {
      if (script.dataset.loaded === 'true') {
        queueMicrotask(handleLoad);
      }
      return;
    }

    script.id = AUTH_TURNSTILE_SCRIPT_ID;
    script.src = AUTH_TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

export default function AuthTurnstile({
  action,
  onTokenChange,
  resetKey = 0,
  configuration = getAuthTurnstileConfiguration(),
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const { enabled, isConfigured, siteKey } = configuration;

  useEffect(() => {
    onTokenChange?.(null);

    if (!enabled) {
      setStatus('idle');
      return undefined;
    }

    if (!isConfigured || !siteKey) {
      setStatus('misconfigured');
      return undefined;
    }

    let isCancelled = false;
    setStatus('loading');

    loadTurnstileScript()
      .then((turnstile) => {
        if (isCancelled || !containerRef.current) return;

        const widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          appearance: 'always',
          size: 'flexible',
          theme: 'dark',
          callback: (token) => {
            if (isCancelled) return;
            setStatus('verified');
            onTokenChange?.(token);
          },
          'expired-callback': () => {
            if (isCancelled) return;
            setStatus('expired');
            onTokenChange?.(null);
          },
          'timeout-callback': () => {
            if (isCancelled) return;
            setStatus('expired');
            onTokenChange?.(null);
          },
          'error-callback': () => {
            if (isCancelled) return;
            setStatus('error');
            onTokenChange?.(null);
          },
        });

        widgetIdRef.current = widgetId;
        setStatus('ready');
      })
      .catch(() => {
        if (!isCancelled) {
          setStatus('error');
          onTokenChange?.(null);
        }
      });

    return () => {
      isCancelled = true;
      const widgetId = widgetIdRef.current;
      const turnstile = getLoadedTurnstile();
      if (widgetId !== null && typeof turnstile?.remove === 'function') {
        turnstile.remove(widgetId);
      }
      widgetIdRef.current = null;
    };
  }, [action, enabled, isConfigured, onTokenChange, siteKey]);

  useEffect(() => {
    if (!enabled || resetKey === 0) return;

    onTokenChange?.(null);
    const widgetId = widgetIdRef.current;
    const turnstile = getLoadedTurnstile();
    if (widgetId !== null && typeof turnstile?.reset === 'function') {
      turnstile.reset(widgetId);
      setStatus('ready');
    }
  }, [enabled, onTokenChange, resetKey]);

  if (!enabled) return null;

  const hasBlockingError = status === 'misconfigured' || status === 'error';
  const statusMessage = hasBlockingError
    ? t('auth.bot_check_unavailable')
    : status === 'expired'
      ? t('auth.bot_check_expired')
      : status === 'loading'
        ? t('auth.bot_check_loading')
        : null;

  return (
    <div className="space-y-2" data-testid="auth-turnstile" data-status={status}>
      <div ref={containerRef} className="min-h-[65px] w-full" />
      {statusMessage ? (
        <p
          className={hasBlockingError ? 'text-xs text-red-300' : 'text-xs text-white/55'}
          role={hasBlockingError ? 'alert' : 'status'}
        >
          {statusMessage}
        </p>
      ) : null}
    </div>
  );
}
